import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr0 } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { DoorOpen, Building2, IndianRupee, MapPin } from "lucide-react";

export default function VacantUnits() {
  const [data, setData] = useState(null);
  const load = () => api.get("/reports/vacant-units").then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-slate-400 text-sm p-4">Loading…</div>;
  const { summary, properties } = data;

  return (
    <div>
      <PageHeader title="Vacant Units" subtitle="Units available to rent, grouped by property" testid="vacant-units-header" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="border-slate-200 shadow-sm p-4 flex items-center gap-3" data-testid="vacant-stat-units">
          <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center"><DoorOpen className="w-5 h-5 text-rose-600" /></div>
          <div><p className="text-2xl font-heading font-bold text-slate-900">{summary.vacant_units}</p><p className="text-xs text-slate-500">Vacant units</p></div>
        </Card>
        <Card className="border-slate-200 shadow-sm p-4 flex items-center gap-3" data-testid="vacant-stat-properties">
          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center"><Building2 className="w-5 h-5 text-amber-600" /></div>
          <div><p className="text-2xl font-heading font-bold text-slate-900">{summary.properties_with_vacancy}</p><p className="text-xs text-slate-500">Properties with vacancy</p></div>
        </Card>
        <Card className="border-slate-200 shadow-sm p-4 flex items-center gap-3" data-testid="vacant-stat-rent">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center"><IndianRupee className="w-5 h-5 text-indigo-600" /></div>
          <div><p className="text-2xl font-heading font-bold text-slate-900">{inr0(summary.expected_rent)}</p><p className="text-xs text-slate-500">Expected rent / month (vacant)</p></div>
        </Card>
      </div>
      {properties.length === 0 ? <EmptyState icon={DoorOpen} title="No vacant units" hint="Every unit is currently occupied." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="vacant-properties-grid">
          {properties.map((p) => (
            <Card key={p.property_id} className="border-slate-200 shadow-sm" data-testid={`vacant-property-${p.property_id}`}>
              <div className="p-4 border-b border-slate-100">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-heading font-semibold text-slate-800 leading-tight">{p.property}</h3>
                    {(p.address || p.city) && <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" />{[p.address, p.city].filter(Boolean).join(", ")}</p>}
                  </div>
                  <span className="text-xs px-2 py-1 rounded-md bg-rose-50 text-rose-600 font-medium whitespace-nowrap">{p.vacant_count} of {p.total_units} vacant</span>
                </div>
              </div>
              <div className="p-3 space-y-2">
                {p.vacant_units.map((u, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50/50 px-3 py-2" data-testid={`vacant-unit-${p.property_id}-${i}`}>
                    <div className="flex items-center gap-2">
                      <DoorOpen className="w-4 h-4 text-rose-500" />
                      <span className="text-sm font-medium text-slate-800">{u.unit_no}</span>
                    </div>
                    <div className="text-right">
                      {u.rent > 0 && <p className="text-sm font-semibold text-slate-700">{inr0(u.rent)}<span className="text-[10px] text-slate-400 font-normal">/mo</span></p>}
                      {u.deposit > 0 && <p className="text-[10px] text-slate-400">Dep {inr0(u.deposit)}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
